/*
 * pie.js
 *
 * Canvas pie charts for Home and Samen Overzicht. Behaviour mirrors
 * the original artifact one-to-one. Draws after the next animation
 * frame so the canvas is in the DOM before we grab its 2D context.
 */

import { state } from './state.js';
import { homeSegs, samenSegs, inkomstenSegs, themeRecolor, getActiveMode } from './helpers.js';

/**
 * Find each visible pie canvas and (re)draw it. Also updates the
 * prev/next month buttons' disabled state on the home pie.
 *
 * Call after `render()` or `switchTab()` via requestAnimationFrame
 * so the new DOM has settled.
 */
export function drawAllPies() {
  // Home pie — gauge-stijl: slices vullen alleen tot (uitgegeven / inkomen).
  // De rest van de ring blijft leeg → meteen visueel hoe vol je inkomen al op is.
  const pieHome = document.getElementById('pieHome');
  if (pieHome) {
    const seg = homeSegs(state.person);
    const cats = themeRecolor(seg.cats);
    const slices = cats.map(c => ({ a: c.amount, c: c.color }));
    // Totaal = inkomen als positief, anders fallback naar de optelsom
    // zodat een persoon zonder loon nog steeds een gevulde pie krijgt.
    const total = seg.inkomst > 0 ? seg.inkomst : null;
    drawPie(pieHome, slices, total);
  }

  // Samen pie — gegroepeerd per categorie. Geen resterend (geen inkomen).
  const pieSamen = document.getElementById('pieSamen');
  if (pieSamen) {
    const seg = samenSegs();
    const cats = themeRecolor(seg.cats);
    drawPie(pieSamen, cats.map(c => ({ a: c.amount, c: c.color })));
  }

  // Inkomsten pie — verdeling van het binnenkomende geld per categorie.
  const pieInk = document.getElementById('pieInkomsten');
  if (pieInk) {
    const seg = inkomstenSegs(state.person);
    const cats = themeRecolor(seg.cats);
    drawPie(pieInk, cats.map(c => ({ a: c.amount, c: c.color })));
  }

  // Month-nav buttons: enabled/disabled based on viewable range.
  updateMonthNavButtons();
}

function updateMonthNavButtons() {
  const mp = document.getElementById('mprev');
  const mn = document.getElementById('mnext');
  if (!mp || !mn) return;

  // Minimum: 3 months before today.
  let minM = state.TODAY.getMonth() - 3;
  let minY = state.TODAY.getFullYear();
  while (minM < 0) { minM += 12; minY--; }

  mp.disabled =
    (state.vY === minY && state.vM === minM) ||
    (state.vY < minY);
  mn.disabled =
    state.vY === state.TODAY.getFullYear() &&
    state.vM === state.TODAY.getMonth();
}

/**
 * Draw a donut chart on the given canvas.
 *
 * @param {HTMLCanvasElement} canvas       130x130 px canvas.
 * @param {{a:number, c:string}[]} segs    Segments with amount + colour.
 * @param {number|null} [total]            Optional denominator for the arc.
 *   - null (default): slices fill the full ring (sum to 360°).
 *   - getal: slices vullen (sum / total) van de ring; de rest blijft leeg
 *     (gauge-stijl). Bij sum > total wordt de ring sowieso vol.
 */
export function drawPie(canvas, segs, total = null) {
  segs = segs.filter(s => s.a > 0);
  const ctx = canvas.getContext('2d');
  const w = canvas.width;
  const h = canvas.height;
  const cx = w / 2;
  const cy = h / 2;
  const rOuter = Math.min(w, h) / 2 - 10;
  const rInner = rOuter * 0.51;
  // Volg de gekozen modus, niet de OS-voorkeur — anders staat de pie nog
  // in dark mode op een desktop met OS-dark terwijl de gebruiker light koos.
  const dark = getActiveMode() === 'dark';

  ctx.clearRect(0, 0, w, h);

  const sum  = segs.reduce((s, x) => s + x.a, 0) || 1;
  const denom = total != null ? Math.max(total, sum) : sum;

  // Achtergrondring zichtbaar bij gauge-stijl OF wanneer er geen slices zijn —
  // anders zou de canvas als 'leeg' (alleen donut-gat) verschijnen.
  if (total != null || segs.length === 0) {
    ctx.beginPath();
    ctx.arc(cx, cy, rOuter, 0, Math.PI * 2);
    ctx.fillStyle = dark ? '#2a2a2a' : '#eef0f3';
    ctx.fill();
  }

  let angle = -Math.PI / 2;
  segs.forEach(s => {
    const sl = (s.a / denom) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, rOuter, angle, angle + sl);
    ctx.closePath();
    ctx.fillStyle = s.c;
    ctx.fill();
    angle += sl;
  });

  // Donut hole — matched to theme background.
  ctx.beginPath();
  ctx.arc(cx, cy, rInner, 0, Math.PI * 2);
  ctx.fillStyle = dark ? '#1a1a1a' : '#ffffff';
  ctx.fill();
}
